import { useTranslation } from 'react-i18next'

import { EntityStatus } from '../../../Entity'
import { APPLICANT_STAGES } from '../../../RentalPortalApplicant'
import Text from '../../../Text'
import { Widget } from '../../../Widget'
import { useApplicantProfileContext } from '../RentalPortalApplicantProfile.hooks'

import { ApplicantProfileWidgetStageScreeningRow } from './ApplicantProfileWidgetStageScreeningRow'
import { ApplicantProfileWidgetStageScreeningSkeleton } from './ApplicantProfileWidgetStageScreeningSkeleton'
import {
  APPLICANT_STAGE_SCREENING_GROUP_TITLE_KEY,
  APPLICANT_STAGE_SCREENING_TITLE_SEPARATOR,
  QA_NAME_APPLICANT_PROFILE_SCREENING_COUNT,
  QA_NAME_APPLICANT_PROFILE_STAGE_MIRROR,
  QA_NAME_APPLICANT_PROFILE_WIDGET_STAGE_SCREENING,
} from './RentalPortalApplicantProfileWidgetStageScreening.constants'
import {
  getApplicantMarkedCheckKey,
  getApplicantPoolChecks,
  getApplicantScreeningCount,
  getApplicantStageScreeningGroups,
  getIsApplicantScreeningGroup,
  getIsApplicantScreeningStarted,
  toApplicantConnectedChecks,
} from './RentalPortalApplicantProfileWidgetStageScreening.utils'

import * as styles from './RentalPortalApplicantProfileWidgetStageScreening.module.css'

function RentalPortalApplicantProfileWidgetStageScreening() {
  const { t } = useTranslation()

  const { applicantData, isInitiated } = useApplicantProfileContext()

  const groups = getApplicantStageScreeningGroups({ applicantData, t })

  const markedCheckKey = getApplicantMarkedCheckKey({
    applicantData,
    checkKeys: groups.flatMap(group => group.checks).map(check => check.key),
  })

  const poolChecks = getApplicantPoolChecks(applicantData)

  return (
    <Widget
      contentContainerProps={{
        Skeleton: ApplicantProfileWidgetStageScreeningSkeleton,
        className: styles.content,
      }}
      headerProps={{ contentProps: { actionsProps: { EditButton: null } } }}
      isSkeleton={!isInitiated || !applicantData}
      qaName={QA_NAME_APPLICANT_PROFILE_WIDGET_STAGE_SCREENING}
      title={t('RentalPortal.ApplicantProfileStageScreeningWidgetTitleKey')}
    >
      <div className={styles.stageRow}>
        <Text isSecondary className={styles.stageLabel}>
          {t('RentalPortal.ApplicantProfileStageMirrorLabelKey')}
        </Text>
        <EntityStatus
          id={applicantData?.stage}
          statuses={APPLICANT_STAGES}
          textQaName={QA_NAME_APPLICANT_PROFILE_STAGE_MIRROR}
        />
      </div>

      {getIsApplicantScreeningStarted(applicantData) ? (
        groups.map(({ checks, key }) => (
          <div className={styles.group} key={key}>
            <div className={styles.groupTitle}>
              <Text isSecondary className={styles.groupTitleText}>
                {t(APPLICANT_STAGE_SCREENING_GROUP_TITLE_KEY[key])}
              </Text>
              {getIsApplicantScreeningGroup(key) ? (
                <>
                  <Text isSecondary className={styles.groupTitleText}>
                    {APPLICANT_STAGE_SCREENING_TITLE_SEPARATOR}
                  </Text>
                  <Text
                    isSecondary
                    className={styles.groupTitleText}
                    qaName={QA_NAME_APPLICANT_PROFILE_SCREENING_COUNT}
                  >
                    {t(
                      'RentalPortal.ApplicantProfileScreeningCountKey',
                      getApplicantScreeningCount(checks)
                    )}
                  </Text>
                </>
              ) : null}
            </div>
            <div className={styles.checks}>
              {checks.map(({ key: checkKey, ...check }) => (
                <ApplicantProfileWidgetStageScreeningRow
                  {...check}
                  isNeedsYou={checkKey === markedCheckKey}
                  key={checkKey}
                />
              ))}
            </div>
          </div>
        ))
      ) : poolChecks.length > 0 ? (
        <div className={styles.group}>
          <div className={styles.groupTitle}>
            <Text isSecondary className={styles.groupTitleText}>
              {applicantData?.phase?.label}
            </Text>
            <Text isSecondary className={styles.groupTitleText}>
              {APPLICANT_STAGE_SCREENING_TITLE_SEPARATOR}
            </Text>
            <Text isSecondary className={styles.groupTitleText}>
              {t(
                'RentalPortal.ApplicantProfileScreeningCountKey',
                getApplicantScreeningCount(poolChecks)
              )}
            </Text>
          </div>
          <div className={styles.checks}>
            {toApplicantConnectedChecks(poolChecks).map((check, index) => (
              <ApplicantProfileWidgetStageScreeningRow {...check} key={index} />
            ))}
          </div>
        </div>
      ) : (
        <Text isSecondary className={styles.poolHint}>
          {t('RentalPortal.ApplicantProfileScreeningPoolHintKey')}
        </Text>
      )}
    </Widget>
  )
}

export default RentalPortalApplicantProfileWidgetStageScreening
